import React, { useState, useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { ReactComponent as SearchGlass } from '../../Assets/images/svg/dar_search.svg';
import PropTypes from 'prop-types';
import './multiFilterSearch.scss';
import ClearIcon from '@mui/icons-material/Clear';
import { ReactComponent as FilterIcon } from '../../Assets/images/svg/dar_filter_icon.svg';
import { ReactComponent as ArrowDownIcon } from '../../Assets/images/svg/dar_dropdown_arrow.svg';
import {ReactComponent as ArrowDownDarkIcon} from '../../Assets/images/svg/Path.svg';
import { SEARCH } from '../../Constants/strings';
import { ReactComponent as SelectedFilterIcon } from '../../Assets/images/svg/dar_filter_selected_icon.svg';

// Custom component to wrap ArrowDownIcon and apply margin
const CustomArrowDownIcon = (props) => (
  <ArrowDownIcon {...props} style={{ right: '1%' }} />
);
const CustomArrowDownDarkIcon = (props) => (
  <ArrowDownDarkIcon {...props} style={{ right: '1%' }} />
);

const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

const MultiSelectSearchDropdown = ({
  options = [],
  onChange,
  placeholder,
  value = [],
  dropdownCss,
  searchBox = true,
  disabled,
  clearIcon = true,
  SearchPlaceHolder = SEARCH,
  displayOption,
  showFilterIcon = true,
  mode = 'dropdown',
  checkboxClassName = '',
}) => {
  const [searchText, setSearchText] = useState('');

  const displayedOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : []).filter((option) =>
        containsText(option.name, searchText),
      ),
    [searchText, options],
  );

  const handleClear = (event) => {
    event.stopPropagation();
    onChange({ target: { value: [] } });
  };

  const handleChange = (event) => {
    onChange(event);
  };

  const isItemSelected = value.length > 0;

  return (
    <Box className={`common-multi-select-dropdown-root ${dropdownCss}`}>
      <Select
        className={mode === 'dropdown' ? 'multi-select-common-dropdown' : 'single-select-listbox'}
        MenuProps={{
          PaperProps: {
            className: 'multi-select-dropdown-paper',
            sx: {
              maxHeight: 320,
              overflowY: 'auto',
            },
          },
          MenuListProps: {
            sx: {
              maxHeight: 320,
              overflowY: 'auto',
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
          autoFocus: false,
        }}
        id="multi-select-search-dropdown"
        IconComponent={mode === 'dropdown' ? CustomArrowDownIcon : CustomArrowDownDarkIcon} // Use the custom component here
        value={value}
        onChange={handleChange}
        onClose={() => setSearchText('')}
        displayEmpty={true}
        multiple={mode === 'dropdown'}
        renderValue={(selected) => {
          let displayValue = '';
          if (mode === 'dropdown') {
            displayValue = selected.length
              ? selected.map((val) => val.name).join(', ')
              : <span className="multi-select-dropdown-placeholder">{placeholder}</span>;
          } else {
            displayValue = selected?.name
              ? selected.name
              : <span className="multi-select-dropdown-placeholder">{placeholder}</span>;
          }
          return displayValue;
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none', // Remove the default border
            },
            borderBottom: '0.1rem solid #b6b4b4', // Apply the bottom border
            borderRadius: '0', // Ensure the border-radius is reset
            paddingLeft: '0',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none', // Ensure the outline is removed
          },
        }}
        startAdornment={
          showFilterIcon && (
            <InputAdornment position="start">
              {isItemSelected ? <SelectedFilterIcon /> : <FilterIcon />}
            </InputAdornment>
          )
        }
        endAdornment={
          value.length && clearIcon ? (
            <InputAdornment className="multi-select-dropdown-clear-icon">
              <IconButton onClick={handleClear} size="small">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        fullWidth
        size="small"
        disabled={disabled}
      >
        <ListSubheader>
          {searchBox && (
            <TextField
              className="multi-select-dropdown-text-field"
              size="small"
              autoFocus
              placeholder={`${SearchPlaceHolder}...`}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchGlass />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') {
                  event.stopPropagation();
                }
              }}
            />
          )}
        </ListSubheader>
        {options.length <= 0 ? (
          <MenuItem disabled className="loading-options">
            <CircularProgress size={24} />
          </MenuItem>
        ) : (
          displayedOptions.map((option) => (
            <MenuItem
              className="multi-select-dropdown-menu-item"
              key={option.id}
              value={option}
            >
              {mode === 'dropdown' && (
                <Checkbox
                  className={`select-checkbox ${checkboxClassName}`}
                  checked={value.indexOf(option) > -1}
                />
              )}
              {displayOption ? displayOption(option) : option.name}
            </MenuItem>
          ))
        )}

      </Select>
    </Box >
  );
};

MultiSelectSearchDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.any,
  value: PropTypes.object,
  dropdownCss: PropTypes.string,
  searchBox: PropTypes.bool,
  disabled: PropTypes.bool,
  clearIcon: PropTypes.bool,
  SearchPlaceHolder: PropTypes.string,
  displayOption: PropTypes.func,
  showFilterIcon: PropTypes.bool,
  mode: PropTypes.string,
  checkboxClassName: PropTypes.string,
};

export default MultiSelectSearchDropdown;
